import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import { showToast } from '../components/Toast';
import Icon from '../components/Icon';
import { useFunnelAnalytics } from '../hooks/useFunnelAnalytics';
import { EVENTS } from '../constants/events';

/**
 * "Where should we send it?" — the shipping step for the free printed
 * storybook, at /funnel-auth/book-address.
 *
 * A step of its own, placed AFTER checkout in the RevenueCat funnel. The book
 * itself is claimed much earlier (FunnelBookStep, pre-sign-in) and starts
 * generating immediately; only the address waits until here. Asking a stranger
 * mid-sign-up for their home address costs more sign-ups than it saves round
 * trips — by this point they've paid, so the ask is easy.
 *
 * Contract, identical to /funnel-auth: RevenueCat passes `redirect_uri` and
 * `state`; on success we hand control back through the same
 * /auth/funnel/callback exchange, which returns the funnel URL to continue to.
 *
 * The route lives under /funnel-auth because that prefix already resolves at
 * CloudFront's edge and is allowlisted in Supabase — the same reason
 * /funnel-auth/intro exists. No infra change needed to ship this.
 *
 * Identity comes from the signed-in session: they authenticated during the
 * funnel, so the JWT is in localStorage by the time they arrive.
 */

// Lulu prints and ships worldwide; this short list covers almost every
// visitor, with an escape hatch. `state` only matters in the ones that use it
// for addressing.
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'OTHER', name: 'Somewhere else' },
];
const STATE_REQUIRED = new Set(['US', 'CA', 'AU']);

const EMPTY_SHIPPING = {
  name: '', line1: '', line2: '', city: '',
  state: '', postalCode: '', country: 'US', phone: '',
};

// Survives a reload mid-form. Cleared once the address is accepted.
const DRAFT_KEY = 'rc_funnel_book_address_draft';

// Mirrors _parse_shipping() in routers/print_orders_router.py, state rule included.
const validateShipping = (shipping) => {
  const errors = {};
  const req = (field, message) => {
    if (!String(shipping[field] || '').trim()) errors[field] = message;
  };
  req('name', 'Who should we address it to?');
  req('line1', 'We need a street address');
  req('city', 'Add a city');
  req('postalCode', 'Add a postal code');
  req('country', 'Pick a country');
  // Couriers reject international labels without one, and a failed label is a
  // book that silently never arrives — so this is required, not optional.
  req('phone', 'Couriers need a phone number');
  if (STATE_REQUIRED.has(shipping.country)) req('state', 'Add a state or province');
  return errors;
};

const FunnelBookAddressPage = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading, user, signInWithGoogle } = useAuth();
  const { apiClient } = useAPI();

  const [shipping, setShipping] = useState(() => {
    try {
      return { ...EMPTY_SHIPPING, ...JSON.parse(sessionStorage.getItem(DRAFT_KEY) || '{}') };
    } catch {
      return { ...EMPTY_SHIPPING };
    }
  });
  const [errors, setErrors] = useState({});
  const [order, setOrder] = useState(null);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Same param handling as the auth step: RevenueCat sends redirect_uri/state
  // on arrival; a Google round trip returns them under funnel_-prefixed names
  // so they don't collide with the OAuth state/code Supabase appends.
  const redirectUri =
    searchParams.get('redirect_uri') || searchParams.get('funnel_redirect_uri');
  const funnelState =
    searchParams.get('state') || searchParams.get('funnel_state');
  const invalidLink = !redirectUri || !funnelState;

  const funnel = useFunnelAnalytics(
    { print_order_status: order?.print_order_status || null },
    {
      funnelName: 'web2app_book',
      viewOrder: ['book_address', 'book_address_done'],
      pathPrefix: '/funnel-auth/book-address',
      startedKey: 'rc_funnel_book_address_started',
    }
  );

  useEffect(() => {
    if (invalidLink) {
      funnel.trackInvalidLink();
      return;
    }
    funnel.trackView('book_address');
  }, [invalidLink, funnel]);

  // Which book they claimed — so the page can name it rather than saying
  // "your book". Best-effort: the form works regardless.
  useEffect(() => {
    if (loading || !isAuthenticated || !apiClient || orderLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient.getFunnelFreeBook();
        if (!cancelled) setOrder(result?.data || null);
      } catch (error) {
        logger.warn('Could not load the free-book order (non-blocking):', error);
      } finally {
        if (!cancelled) setOrderLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, isAuthenticated, apiClient, orderLoaded]);

  // Prefill the recipient from the account once, if they haven't typed a name.
  useEffect(() => {
    if (!user) return;
    setShipping((prev) => {
      if (prev.name) return prev;
      const guess = user.username || user.full_name || '';
      return guess ? { ...prev, name: guess } : prev;
    });
  }, [user]);

  const bookTitle = useMemo(() => order?.book_title || null, [order]);

  const setField = (field) => (e) => {
    const next = { ...shipping, [field]: e.target.value };
    setShipping(next);
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    // Clear a field's error the moment it's touched — re-running the whole
    // validation on each keystroke lights up fields they haven't reached.
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Hand control back to RevenueCat exactly as the auth step does.
  const returnToFunnel = useCallback(async () => {
    try {
      const result = await apiClient.completeFunnelAuth(redirectUri, funnelState);
      if (result.status && result.data?.redirect_url) {
        window.location.replace(result.data.redirect_url);
        return;
      }
      throw new Error(result.error || 'Could not return to the funnel');
    } catch (error) {
      // The address IS saved by this point — never make them retype it because
      // the hand-off failed. Show the confirmation and let them continue.
      logger.error('Funnel hand-off after address save failed:', error);
      funnel.track(EVENTS.FUNNEL_BOOK_ADDRESS_HANDOFF_FAILED, {
        error_message: error.message || 'unknown',
      });
      setSaved(true);
    }
  }, [apiClient, redirectUri, funnelState, funnel]);

  const submit = async () => {
    const found = validateShipping(shipping);
    if (Object.keys(found).length) {
      setErrors(found);
      funnel.track(EVENTS.FUNNEL_BOOK_ADDRESS_INVALID, { fields: Object.keys(found).join(',') });
      return;
    }
    setSaving(true);
    try {
      const result = await apiClient.saveFunnelBookAddress(shipping);
      sessionStorage.removeItem(DRAFT_KEY);
      funnel.trackStepCompleted('book_address', {
        country: shipping.country,
        print_order_status: result?.data?.print_order_status || null,
      });
      await returnToFunnel();
    } catch (error) {
      logger.error('Saving the shipping address failed:', error);
      funnel.track(EVENTS.FUNNEL_BOOK_ADDRESS_FAILED, {
        error_message: error.message || 'unknown',
        status_code: error.status || null,
      });
      showToast(
        error.status === 404
          ? "We couldn't find a free book on your account."
          : "We couldn't save your address. Please try again.",
        'error'
      );
      setSaving(false);
    }
  };

  // Skipping keeps the order parked rather than cancelling it — they can still
  // send an address later, and the storybook itself is unaffected.
  const skip = async () => {
    funnel.track(EVENTS.FUNNEL_BOOK_ADDRESS_SKIPPED, {});
    setSaving(true);
    await returnToFunnel();
  };

  const signIn = async () => {
    // Come back to this exact step, params intact.
    const returnUrl = new URL('/funnel-auth/book-address', window.location.origin);
    if (redirectUri) returnUrl.searchParams.set('funnel_redirect_uri', redirectUri);
    if (funnelState) returnUrl.searchParams.set('funnel_state', funnelState);
    await signInWithGoogle(returnUrl.toString());
  };

  const field = (key, label, props = {}) => (
    <div className={`fb-field${props.wide ? ' fb-wide' : ''}`}>
      <label className="fb-label" htmlFor={`fb-${key}`}>
        {label}
        {props.optional && <span className="fb-optional">Optional</span>}
      </label>
      <input
        id={`fb-${key}`}
        className={`fb-input${errors[key] ? ' has-error' : ''}`}
        value={shipping[key] || ''}
        onChange={setField(key)}
        autoComplete={props.autoComplete}
        inputMode={props.inputMode}
        type={props.type || 'text'}
        placeholder={props.placeholder}
        maxLength={props.maxLength || 120}
        disabled={saving}
        aria-invalid={!!errors[key]}
        aria-describedby={errors[key] ? `fb-${key}-err` : undefined}
      />
      {errors[key] && <span className="fb-error" id={`fb-${key}-err`}>{errors[key]}</span>}
    </div>
  );

  const body = () => {
    if (invalidLink) {
      return (
        <>
          <img className="ba-panda ba-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
          <h1 className="fa-title">Hmm, this link isn't quite right</h1>
          <p className="fa-subtitle">
            This page is part of the Pixley sign-up flow. Head to{' '}
            <a href="https://www.pixleyai.com">pixleyai.com</a> to get started.
          </p>
        </>
      );
    }

    if (loading) {
      return (
        <>
          <div className="ba-spinner" />
          <p className="fa-subtitle">One moment…</p>
        </>
      );
    }

    if (!isAuthenticated) {
      return (
        <>
          <img className="ba-panda ba-float" src="/assets/pixley-panda-nobg.png" alt="Pixley Panda" />
          <div className="fa-eyebrow">Almost there</div>
          <h1 className="fa-title">Sign in to claim your book</h1>
          <p className="fa-subtitle">
            We just need to know which account the free storybook belongs to.
          </p>
          <button className="fa-primary-btn" onClick={signIn}>
            Continue with Google <span className="fa-chevron">›</span>
          </button>
        </>
      );
    }

    if (saved) {
      return (
        <>
          <div className="ba-success"><Icon name="local_shipping" size={40} /></div>
          <h1 className="fa-title">Your book is on its way</h1>
          <p className="fa-subtitle">
            We'll print {bookTitle ? <strong>{bookTitle}</strong> : 'your storybook'} as soon as
            the illustrations are finished, then post it to you.
          </p>
          <button
            className="fa-primary-btn"
            onClick={() => window.location.replace('https://www.pixleyai.com')}
          >
            Back to Pixley <span className="fa-chevron">›</span>
          </button>
        </>
      );
    }

    return (
      <div className="ba-form-wrap">
        <div className="ba-gift">
          <span className="ba-gift-icon"><Icon name="redeem" size={19} fill /></span>
          <span className="ba-gift-text">
            <strong>Your free hardcover</strong>
            <small>{bookTitle ? bookTitle : 'Printed and posted, on us'}</small>
          </span>
        </div>

        <div className="fa-eyebrow">Free shipping</div>
        <h1 className="fa-title">
          Where should we <span className="ba-accent">send it</span>?
        </h1>
        <p className="fa-subtitle">
          Your storybook is being illustrated right now. Tell us where to post the printed
          copy and we'll take care of the rest.
        </p>

        <div className="fb-form">
          {field('name', 'Send it to', { autoComplete: 'name', placeholder: 'Full name', wide: true })}
          {field('line1', 'Address', { autoComplete: 'address-line1', placeholder: 'Street address', wide: true })}
          {field('line2', 'Apartment, suite', { autoComplete: 'address-line2', placeholder: 'Apt 4B', optional: true, wide: true })}
          {field('city', 'City', { autoComplete: 'address-level2', placeholder: 'City' })}
          {field('state', 'State / Province', { autoComplete: 'address-level1', placeholder: 'State' })}
          {field('postalCode', 'Postal code', { autoComplete: 'postal-code', placeholder: 'ZIP', maxLength: 16 })}

          <div className="fb-field">
            <label className="fb-label" htmlFor="fb-country">Country</label>
            <select
              id="fb-country"
              className="fb-input fb-select"
              value={shipping.country || 'US'}
              onChange={setField('country')}
              autoComplete="country"
              disabled={saving}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>

          {field('phone', 'Phone', {
            autoComplete: 'tel', inputMode: 'tel', type: 'tel',
            placeholder: 'For the courier', maxLength: 24, wide: true,
          })}
        </div>

        <button className="fa-primary-btn" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : <>Send me my book <span className="fa-chevron">›</span></>}
        </button>

        <button type="button" className="ba-skip" onClick={skip} disabled={saving}>
          I'll add this later
        </button>

        <p className="ba-privacy">
          <Icon name="lock" size={12} /> Used only to post your book. Never shared.
        </p>
      </div>
    );
  };

  return (
    <MotionConfig reducedMotion="user">
      <FunnelBookAddressStyles />
      <div className="ba-bg">
        <div className="ba-shell">
          <AnimatePresence mode="wait">
            <motion.div
              key={invalidLink ? 'invalid' : !isAuthenticated ? 'signin' : saved ? 'saved' : 'form'}
              className="fa-card ba-card"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {body()}
            </motion.div>
          </AnimatePresence>
          <p className="ba-footer">PIXLEY</p>
        </div>
      </div>
    </MotionConfig>
  );
};

// Standalone page, so it restates the funnel shell (.fa-card etc. live in
// FunnelAuthPage's <style>, which isn't mounted here). Palette follows
// NewOnboardingKit.swift's NOTheme, same as the rest of the funnel.
const FunnelBookAddressStyles = () => (
  <style>{`
    .ba-bg {
      position: relative;
      min-height: 100vh; min-height: 100dvh;
      background:
        radial-gradient(1100px 750px at 85% -10%, rgba(124,92,255,0.10), transparent 60%),
        radial-gradient(950px 700px at -10% 110%, rgba(10,108,255,0.10), transparent 60%),
        linear-gradient(180deg, #F5FAFF 0%, #E8F1FF 100%);
      font-family: 'Inter', 'Fredoka', sans-serif;
      display: flex;
      padding: calc(32px + env(safe-area-inset-top, 0px))
               calc(16px + env(safe-area-inset-right, 0px))
               calc(32px + env(safe-area-inset-bottom, 0px))
               calc(16px + env(safe-area-inset-left, 0px));
      box-sizing: border-box;
    }
    .ba-shell { width: 100%; max-width: 460px; margin: auto; }
    .ba-card {
      position: relative;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      border: 1px solid rgba(232,237,245,0.9);
      border-radius: 28px;
      box-shadow: 0 24px 60px rgba(11,26,51,0.10), 0 4px 14px rgba(11,26,51,0.05);
      padding: 36px 30px 32px;
      text-align: center;
    }
    .ba-form-wrap { text-align: left; }
    .ba-accent { color: #0065F8; }

    .fa-eyebrow {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 13px; font-weight: 700;
      letter-spacing: 2px; text-transform: uppercase;
      color: #0A6CFF; margin-bottom: 10px;
    }
    .fa-title {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 29px; font-weight: 700;
      color: #0B1A38; line-height: 1.15;
      letter-spacing: -0.3px;
      margin: 0 0 9px;
    }
    .fa-subtitle { font-size: 16px; color: #6C7A8F; line-height: 1.55; margin: 0 0 22px; }
    .fa-subtitle a { color: #0A6CFF; }

    .fa-primary-btn {
      width: 100%;
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      margin-top: 22px; padding: 18px 22px;
      border: none; border-radius: 18px;
      background: #0A6CFF; color: #FFFFFF;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 18px; font-weight: 700;
      cursor: pointer;
      box-shadow: 0 8px 16px rgba(10,108,255,0.35);
      transition: transform 0.18s ease, opacity 0.18s ease;
      -webkit-tap-highlight-color: transparent; touch-action: manipulation;
    }
    .fa-primary-btn:hover:not(:disabled) { transform: translateY(-1px); }
    .fa-primary-btn:active:not(:disabled) { transform: scale(0.98); }
    .fa-primary-btn:disabled { opacity: 0.45; cursor: default; box-shadow: none; }
    .fa-chevron { font-size: 20px; line-height: 1; }

    /* ── the gift reminder ──────────────────────────────────────────────── */
    .ba-gift {
      display: flex; align-items: center; gap: 11px;
      margin-bottom: 20px; padding: 11px 14px;
      border-radius: 16px;
      background: linear-gradient(135deg, rgba(255,200,61,0.14), rgba(255,111,165,0.10));
      border: 1px solid rgba(255,200,61,0.42);
    }
    .ba-gift-icon {
      display: flex; align-items: center; justify-content: center;
      width: 36px; height: 36px; flex-shrink: 0;
      border-radius: 11px;
      background: rgba(255,200,61,0.24);
      color: #B07800;
    }
    .ba-gift-text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
    .ba-gift-text strong {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14.5px; font-weight: 600; color: #0B1A38;
    }
    .ba-gift-text small {
      font-size: 12.5px; color: #6C7A8F;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }

    /* ── form (mirrors FunnelBookStep's field styling) ──────────────────── */
    .fb-form { display: grid; grid-template-columns: 1fr 1fr; gap: 11px 10px; width: 100%; }
    .fb-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
    .fb-wide { grid-column: 1 / -1; }
    .fb-label {
      display: flex; align-items: baseline; gap: 6px;
      font-size: 11px; font-weight: 700; color: #5A6B85;
      letter-spacing: 0.06em; text-transform: uppercase;
    }
    .fb-optional {
      font-size: 9.5px; font-weight: 600; color: #A4B0C2;
      letter-spacing: 0.03em; text-transform: none;
    }
    .fb-input {
      width: 100%; padding: 11px 12px;
      box-sizing: border-box;
      border-radius: 12px;
      border: 1.5px solid #E8EDF5;
      background: #FFFFFF;
      font-family: 'Inter', sans-serif;
      font-size: 15px; color: #0B1A38;
      outline: none;
      transition: border-color 0.18s ease, box-shadow 0.18s ease;
      -webkit-tap-highlight-color: transparent;
    }
    .fb-input:focus { border-color: #0A6CFF; box-shadow: 0 0 0 3px rgba(10,108,255,0.14); }
    .fb-input::placeholder { color: #A4B0C2; }
    .fb-input:disabled { background: #F7F9FC; color: #8A97AB; }
    .fb-input.has-error { border-color: #FF4B4B; box-shadow: 0 0 0 3px rgba(255,75,75,0.12); }
    .fb-select {
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236C7A8F' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 10px center;
      background-size: 15px;
      padding-right: 32px; cursor: pointer;
    }
    .fb-error { font-size: 11px; color: #FF4B4B; line-height: 1.3; }

    .ba-skip {
      display: block; margin: 14px auto 0;
      background: none; border: none;
      font-family: 'Inter', sans-serif;
      font-size: 13.5px; font-weight: 600; color: #6C7A8F;
      text-decoration: underline; text-underline-offset: 3px;
      cursor: pointer;
    }
    .ba-skip:hover:not(:disabled) { color: #0B1A38; }
    .ba-skip:disabled { opacity: 0.5; cursor: default; }

    .ba-privacy {
      display: flex; align-items: center; justify-content: center; gap: 5px;
      margin: 16px 0 0;
      font-size: 12px; color: #A4B0C2;
    }

    /* ── states ─────────────────────────────────────────────────────────── */
    .ba-panda { width: 116px; height: auto; margin: 0 auto 14px; display: block; }
    .ba-float { animation: baFloat 3.4s ease-in-out infinite; }
    @keyframes baFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }

    .ba-success {
      display: flex; align-items: center; justify-content: center;
      width: 78px; height: 78px; margin: 4px auto 18px;
      border-radius: 50%;
      background: rgba(52,199,89,0.14);
      color: #34C759;
    }
    .ba-spinner {
      width: 34px; height: 34px; margin: 12px auto 16px;
      border: 3px solid #DDE6F2; border-top-color: #0A6CFF;
      border-radius: 50%;
      animation: baSpin 0.85s linear infinite;
    }
    @keyframes baSpin { to { transform: rotate(360deg); } }

    .ba-footer {
      text-align: center;
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 14px; font-weight: 700;
      color: #8A97AB; letter-spacing: 3px;
      margin: 18px 0 0;
    }

    @media (prefers-reduced-motion: reduce) {
      .ba-float, .ba-spinner { animation: none; }
    }
    @media (max-width: 400px) {
      .ba-card { padding: 30px 22px 26px; }
      .fa-title { font-size: 26px; }
      .fb-form { grid-template-columns: 1fr; }
    }
  `}</style>
);

export default FunnelBookAddressPage;
